import React, { useRef } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import { features, fadeInUp, staggerContainer } from './landingConstants';
import './LandingFeaturesSection.css';

const LandingFeaturesSection: React.FC = () => {
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const olympicLayoutClasses = [
    'lg:col-start-1 lg:row-start-1',
    'lg:col-start-3 lg:row-start-1',
    'lg:col-start-5 lg:row-start-1',
    'lg:col-start-2 lg:row-start-2',
    'lg:col-start-4 lg:row-start-2',
  ];

  React.useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [controls, isInView]);

  return (
    <section
      id="features"
      className="landing-features landing-section-equal relative py-8 sm:py-10 lg:py-14 overflow-hidden bg-slate-950 border-b border-white/5"
    >
      <div className="absolute inset-0 bg-slate-950"></div>
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-20 left-4 sm:left-10 w-16 h-16 sm:w-24 sm:h-24 lg:w-32 lg:h-32 bg-blue-500/10 rounded-full blur-3xl"
          animate={{
            y: [0, -20, 0],
            x: [0, 10, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute top-40 right-4 sm:right-20 w-12 h-12 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-purple-500/10 rounded-full blur-2xl"
          animate={{
            y: [0, 15, 0],
            x: [0, -10, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
        />
        <motion.div
          className="absolute bottom-20 left-1/4 w-20 h-20 sm:w-32 sm:h-32 lg:w-40 lg:h-40 bg-cyan-500/10 rounded-full blur-3xl"
          animate={{
            y: [0, -10, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 4,
          }}
        />
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[length:60px_60px] opacity-30"></div>
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium bg-cyan-500/15 text-cyan-200 border border-cyan-400/20 mb-3 sm:mb-4">
            <span className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-cyan-300 mr-1.5 sm:mr-2"></span>
            Core Features
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-slate-100 mb-4 sm:mb-6">
            Product capabilities built for{' '}
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              outbound sales teams
            </span>
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-slate-300">
            From AI calling to lead intelligence and campaign control, Agent Noda helps teams execute faster with better visibility.
          </p>
        </motion.div>
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 sm:gap-6 lg:gap-8"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          ref={ref}
          animate={controls}
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              variants={fadeInUp}
              className={`group relative bg-slate-900/78 rounded-2xl p-3 sm:p-4 lg:p-6 shadow-[0_12px_28px_-18px_rgba(2,6,23,0.9)] hover:shadow-[0_18px_40px_-24px_rgba(34,211,238,0.35)] transition-all duration-300 border border-white/10 hover:border-transparent lg:col-span-2 ${olympicLayoutClasses[index] ?? ''}`}
            >
              <div
                className={`absolute -inset-0.5 bg-gradient-to-r ${feature.color} rounded-2xl opacity-0 group-hover:opacity-30 blur-sm transition duration-700 group-hover:duration-300`}
              ></div>
              <div className="relative h-full bg-slate-900/70 rounded-xl p-3 sm:p-4 lg:p-6 border border-white/10">
                <div
                  className={`inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg mb-3 sm:mb-4 lg:mb-6 bg-gradient-to-br ${feature.color} text-white`}
                >
                  <feature.icon className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6" />
                </div>
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-slate-100 mb-1.5 sm:mb-2 lg:mb-3">
                  {feature.title}
                </h3>
                <p className="text-xs sm:text-sm lg:text-base text-slate-300">
                  {feature.description}
                </p>
                <p className="mt-3 text-xs font-medium text-cyan-300">
                  Screenshot-ready module
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
        <div className="mt-6 text-center">
          <a href="#" className="landing-back-to-top">
            Back to top
            <span aria-hidden="true">↑</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default LandingFeaturesSection;
